import { useCallback, useEffect, useMemo, useState } from 'react';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridRowAction } from '@/components/KstDataGrid';
import { Dropdownlist } from '@/components/Dropdownlist';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { useTheme } from '@/contexts/ThemeContext';
import { hasValue } from '@/utils/legacy-utils';
import moment from 'moment-timezone';
import { DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT_SHORT_TIME } from '@/constants/formats';
import { PageModal } from '@/components/modals/PageModal';
import { BaseFormModal } from '@/components/BaseFormModal';
import { NPSDPersonScreen } from '@/components/npsd/NPSDPersonScreen';
import { NPSDBusinessScreen } from '@/components/npsd/NPSDBusinessScreen';
import { NPSDClientScreenWrapper } from '@/components/npsd/NPSDClientScreen';
import { TourStopCompletionScreen } from '@/components/screens/TourStopCompletionScreen';
import { CaseReportFormScreen } from '@/components/screens/CaseReportFormScreen';
import { navigationApi } from '@/services/navigation';
import { buildHashPath } from '@/router/route-config';
import { showGeocodePreview, invalidateTrustedLocationCache } from '@/utils/custom-location';

/**
 * Supervisor screen for reviewing addresses flagged as low-confidence by the geocoder.
 *
 * Lists records across the 6 address-bearing entity types (CallCard, Report, TourStop,
 * NPSDPerson, NPSDBusiness, LocationAlert) filterable by addressVerificationStatus
 * (LOW, AREA, MANUAL, HIGH, OFFICER_ON_SCENE for call cards).
 *
 * Each row can be quick-marked as MANUAL-verified (the supervisor visually confirmed the
 * existing coordinate is correct) without changing the underlying address. For records that
 * need address correction, the supervisor opens the record for inline editing.
 *
 * Role-gated server-side to ROLE_REPORT_APPROVAL. Client-side it is a geocodio-pilot
 * feature: the menu link is gated on both the report-approval role AND
 * App.useGeocodingProxy() in app-compat.ts, and this component returns null when the proxy
 * flag is off, so non-pilot companies never see it even via a direct URL.
 */

interface AddressReviewItem {
  entityType: string;
  entityId: number;
  formattedAddress: string;
  companyId: number;
  companyName: string | null;
  status: string;
  lastUpdated: string;
  verifiedAt: string | null;
  verifiedBy: number | null;
  verifiedByFirstName: string | null;
  verifiedByLastName: string | null;
}

const ENTITY_TYPE_OPTIONS: Array<{ value: string; label: string; singular: string }> = [
  { value: '', label: 'All Entity Types', singular: '' },
  { value: 'CALL_CARD', label: 'Call Cards', singular: 'Call Card' },
  { value: 'REPORT', label: 'Case Reports', singular: 'Case Report' },
  { value: 'TOUR_STOP', label: 'Tour Stops', singular: 'Tour Stop' },
  { value: 'NPSD_PERSON', label: 'Persons', singular: 'Person' },
  { value: 'NPSD_BUSINESS', label: 'Businesses', singular: 'Business' },
  { value: 'LOCATION_ALERT', label: 'Call Alerts', singular: 'Call Alert' },
  { value: 'NPSD_CLIENT', label: 'Clients', singular: 'Client' },
  { value: 'NPSD_CLIENT_SITE', label: 'Client Sites', singular: 'Client Site' },
];

// LEGACY is deliberately not offered: it matches nearly every pre-migration record
// (expensive unindexed query) and there is no supervisor action to take on one.
// NO_TRUSTED is not a stored status — it selects the "locations without a verified pin"
// queue (clients/sites whose status is not HIGH/MANUAL). The supervisor action there is
// Place Pin (map preview → MANUAL coordinate via the locationpin endpoints); Mark Verified
// is not offered because these records have no trusted coordinate to bless, and the client
// edit screen can't be used — it is read-only outside the Admin module.
const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'LOW', label: 'Low Confidence' },
  // Call cards only: the pin marks the service area, so there is nothing to mark verified.
  { value: 'AREA', label: 'Address Not Found' },
  { value: 'HIGH', label: 'High Confidence' },
  { value: 'MANUAL', label: 'Manually Verified' },
  // Call cards only: a confirmation is removed, never marked verified.
  { value: 'OFFICER_ON_SCENE', label: 'Confirmed on Scene' },
  { value: 'NO_TRUSTED', label: 'No Verified Location' },
];

// The no-trusted queue only exists for clients and sites.
const NO_TRUSTED_ENTITY_VALUES = ['', 'NPSD_CLIENT', 'NPSD_CLIENT_SITE'];

const DAYS_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
];

function formatEntityType(t: string): string {
  const found = ENTITY_TYPE_OPTIONS.find((o) => o.value === t);
  return found && found.singular ? found.singular : t;
}

const columns: KstDataGridColumn[] = [
  { field: 'entityId', name: 'Id', size: 'hidden' },
  {
    field: 'entityType',
    name: 'Type',
    size: 'col-xs-2',
    template: (e: any) => (hasValue(e) && hasValue(e.entityType) ? formatEntityType(e.entityType) : ''),
  },
  {
    field: 'formattedAddress',
    name: 'Address',
    size: 'col-xs-3',
    template: (e: any) => (hasValue(e) && hasValue(e.formattedAddress) ? e.formattedAddress : '(empty)'),
  },
  {
    field: 'lastUpdated',
    name: 'Last Updated',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.lastUpdated)) {
        return moment(e.lastUpdated).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT_SHORT_TIME);
      }
      return '';
    },
  },
  {
    field: 'verifiedAt',
    name: 'Verified At',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.verifiedAt)) {
        return moment(e.verifiedAt).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT_SHORT_TIME);
      }
      return '';
    },
  },
  {
    field: 'verifiedBy',
    name: 'Verified By',
    size: 'col-xs-2',
    template: (e: any) => {
      if (!hasValue(e) || !hasValue(e.verifiedBy)) return '';
      const name = [e.verifiedByFirstName, e.verifiedByLastName].filter(Boolean).join(' ');
      // Fallback to the raw id for verifiers whose person record no longer exists.
      return name || String(e.verifiedBy);
    },
  },
  {
    field: 'companyId',
    name: 'Company',
    size: 'col-xs-1',
    template: (e: any) => {
      if (!hasValue(e) || !hasValue(e.companyId)) return '';
      return e.companyName || String(e.companyId);
    },
  },
];

// Row actions follow the grid convention used by CaseReportListScreen / NotesScreen:
// Font Awesome icons on the grid's btn-row-action styling. All actions are labeled —
// Mark Verified can't reduce to an unambiguous glyph, and mixing labeled and icon-only
// buttons in one group reads as visually broken.
const openAction: KstDataGridRowAction = {
  name: 'Open',
  icon: 'fa-eye',
  event: 'open',
  class: 'btn-primary',
};

const newWindowAction: KstDataGridRowAction = {
  name: 'New Window',
  icon: 'fa-external-link',
  event: 'openNewWindow',
};

const markVerifiedAction: KstDataGridRowAction = {
  name: 'Mark Verified',
  icon: 'fa-check',
  event: 'markVerified',
};

const undoVerifiedAction: KstDataGridRowAction = {
  name: 'Remove Verification',
  icon: 'fa-undo',
  event: 'undoVerified',
};

// Confirmed on Scene queue only (call cards alone carry that status): undoes the confirmation
// through the call-card locationpin endpoint, which this CAD/MDT screen's role path can reach.
const removeConfirmationAction: KstDataGridRowAction = {
  name: 'Remove confirmation',
  icon: 'fa-undo',
  event: 'removeConfirmation',
};

const historyAction: KstDataGridRowAction = {
  name: 'History',
  icon: 'fa-history',
  event: 'history',
};

// No-trusted queue only: geocode the stored text for a starting pin, let the supervisor
// correct it on the map, and save via the locationpin endpoints — which are CAD/MDT-
// authorized, unlike the client-record edit screen (writes there are Admin-module-only).
const placePinAction: KstDataGridRowAction = {
  name: 'Place Pin',
  icon: 'fa-map-marker',
  event: 'placePin',
};

// Columns for the Verification History modal — mirrors the audit-log grid convention
// (CallCardAuditLogGrid): Date/Time, actor name, the address as it stood at the time
// of the action (so rows stay meaningful after the record's address changes), action badge.
const historyColumns: KstDataGridColumn[] = [
  {
    field: 'dateCreated',
    name: 'Date/Time',
    size: 'col-xs-3',
    template: (e: any) => (hasValue(e) && hasValue(e.dateCreated)
      ? moment(e.dateCreated).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT_SHORT_TIME)
      : ''),
  },
  { field: 'personFirstName', name: 'First', size: 'col-xs-2' },
  { field: 'personLastName', name: 'Last', size: 'col-xs-2' },
  {
    field: 'formattedAddress',
    name: 'Address',
    size: 'col-xs-3',
    template: (e: any) => (hasValue(e) && hasValue(e.formattedAddress) ? e.formattedAddress : ''),
  },
  {
    field: 'action',
    name: 'Action',
    size: 'col-xs-2',
    template: (e: any) => {
      const action = hasValue(e) ? e.action : '';
      const badgeClass = action === 'MARKED' ? 'audit-action-create' : 'audit-action-delete';
      const label = action === 'MARKED' ? 'Verified' : 'Removed';
      return `<span class="audit-action-badge ${badgeClass}">${label}</span>`;
    },
  },
];

/**
 * Build the SPA route path for an entity so it can be opened in a new browser window.
 * Mirrors the in-app navigation targets used by handleOpenEntity, but as a URL instead
 * of a router transition. Section follows the user's current module (CAD vs MDT) where
 * the route exists in both.
 */
function buildEntityPath(entityType: string, entityId: number): string | null {
  const moduleType = ((window as any).getModuleType?.() || 'MDT').toUpperCase();
  const section = moduleType === 'CAD' ? 'CAD' : 'MDT';
  switch (entityType) {
    case 'CALL_CARD':
      return moduleType === 'CAD'
        ? buildHashPath({ section: 'CAD', tab: 'NewCallCard', fn: 'CAD_CallCard', multiModule: true, params: String(entityId) })
        : buildHashPath({ section: 'MDT', tab: 'SelfInitiate', fn: 'MDT_ShowCallCard', multiModule: true, params: String(entityId) });
    case 'REPORT':
      return buildHashPath({ section, fn: 'displayCaseReport', multiModule: true, params: String(entityId), tab: 'CaseReports' });
    case 'NPSD_PERSON':
      return buildHashPath({ section, fn: 'Open_Person', multiModule: true, params: String(entityId), tab: 'Search' });
    case 'NPSD_BUSINESS':
      return buildHashPath({ section, fn: 'Open_Business', multiModule: true, params: String(entityId), tab: 'Search' });
    case 'TOUR_STOP':
      return buildHashPath({ section, fn: 'TourStopCompletion', multiModule: true, params: String(entityId), tab: 'Search' });
    case 'LOCATION_ALERT':
      return buildHashPath({ section: 'ADMIN', tab: 'CallAlertManagement', fn: 'ADMIN_CallAlertManagement', multiModule: true });
    // NPSD_CLIENT / NPSD_CLIENT_SITE have no hash-navigable window function (Open_ClientGroup is
    // not window-registered), so New Window is a no-op for them; the in-modal Open is the path.
    default:
      return null;
  }
}

export function SupervisorAddressReviewScreen() {
  // Theme class wrapper — the themed layout rules are descendant-scoped
  // (.theme-gray-dark .kst-page-container etc.), so the screen root must carry
  // the active theme class. Same pattern as CallAlertManagementScreen.
  const { theme } = useTheme();
  const [items, setItems] = useState<AddressReviewItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('LOW');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [daysFilter, setDaysFilter] = useState<number>(30);
  const [openEntity, setOpenEntity] = useState<{ entityType: string; entityId: number } | null>(null);
  // Verification History modal: null = closed; items null = loading.
  const [historyEntity, setHistoryEntity] = useState<{ entityType: string; entityId: number; items: any[] | null } | null>(null);

  const refresh = useCallback(() => {
    // Address Review is a geocodio-pilot feature. The route is registered globally, so a
    // non-pilot user could reach this screen by typing the URL even though the menu item is
    // hidden. Bail before hitting the API so the feature stays fully reversible via the flag.
    if (!App.useGeocodingProxy()) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const onOk = (result: any) => {
      setItems(result?.items ?? []);
      setLoading(false);
    };
    const onErr = () => {
      setItems([]);
      setLoading(false);
      App.showPopupNotification('Failed to load address review items.', 'error');
    };
    if (statusFilter === 'NO_TRUSTED') {
      // The proactive queue — a different endpoint with no days window (an unpinned
      // location is work regardless of age) and only client/site entity types.
      api.listAddressReviewNoTrustedLocation(
        {
          entityType: NO_TRUSTED_ENTITY_VALUES.includes(entityTypeFilter) && entityTypeFilter
            ? entityTypeFilter
            : undefined,
          limit: 200,
        },
        onOk,
        onErr
      );
      return;
    }
    api.listAddressReview(
      {
        status: statusFilter,
        entityType: entityTypeFilter || undefined,
        days: daysFilter,
        limit: 200,
      },
      onOk,
      onErr
    );
  }, [statusFilter, entityTypeFilter, daysFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleOpenEntity = useCallback((entityType: string, entityId: number) => {
    // CALL_CARD and LOCATION_ALERT screens are too tightly coupled to module/section
    // state (CAD vs MDT, App.currentCC, route-driven loading) to embed cleanly in a
    // PageModal, so they navigate away from this screen instead. This screen lives in
    // the CAD/MDT modules, so the call-card navigation stays within the supervisor's
    // selected module. The other four entity types render inside a PageModal below.
    if (entityType === 'CALL_CARD') {
      navigationApi.toCallCard(entityId);
      return;
    }
    if (entityType === 'LOCATION_ALERT') {
      navigationApi.to({
        section: 'ADMIN',
        tab: 'CallAlertManagement',
        fn: 'ADMIN_CallAlertManagement',
        multiModule: true,
      });
      return;
    }
    if (entityType === 'NPSD_CLIENT_SITE') {
      // Sites have no standalone screen — they live on their parent client's Sites tab. The
      // review row carries only the site id, so resolve the parent from the all-sites list.
      api.listAllClientGroupSite(
        (sites: any[]) => {
          const site = (sites ?? []).find((s: any) => s.id === entityId);
          if (site?.npsdClientId) {
            setOpenEntity({ entityType: 'NPSD_CLIENT', entityId: site.npsdClientId });
          } else {
            App.showPopupNotification('The site\'s client record could not be found.', 'error');
          }
        },
        () => {
          App.showPopupNotification('The site\'s client record could not be found.', 'error');
        }
      );
      return;
    }
    setOpenEntity({ entityType, entityId });
  }, []);

  // Place Pin (no-trusted queue): geocode the stored address for a starting position, open the
  // map preview for correction, and save the result with MANUAL provenance via the locationpin
  // endpoint for the row's entity type. The record's address text is never changed.
  const handlePlacePin = useCallback((row: AddressReviewItem) => {
    api.geocodeViaProxy(
      { query: row.formattedAddress, companyId: row.companyId },
      async (result: any) => {
        const hasCoords = typeof result?.lat === 'number' && typeof result?.lng === 'number';
        let decision: { lat: number; lng: number } | null = null;
        try {
          decision = await showGeocodePreview({
            mode: hasCoords ? 'low-confidence' : 'no-result',
            formatted: hasCoords ? (result?.formatted ?? row.formattedAddress) : undefined,
            lat: hasCoords ? result.lat : undefined,
            lng: hasCoords ? result.lng : undefined,
            accuracyType: result?.accuracyType ?? undefined,
            alternates: result?.alternates ?? [],
            query: row.formattedAddress,
            requireConfirm: true,
          });
        } catch {
          return; // preview cancelled — nothing saved, row stays in the queue
        }
        if (!decision || typeof decision.lat !== 'number' || typeof decision.lng !== 'number'
            || !isFinite(decision.lat) || !isFinite(decision.lng)) {
          App.showPopupNotification('The location was not saved. A map pin is required.', 'warning');
          return;
        }
        const savePin = row.entityType === 'NPSD_CLIENT_SITE'
          ? api.writeBackSiteLocationPin.bind(api)
          : api.writeBackClientLocationPin.bind(api);
        savePin(
          row.entityId,
          decision.lat,
          decision.lng,
          () => {
            setItems((prev) =>
              prev.filter((it) => !(it.entityType === row.entityType && it.entityId === row.entityId))
            );
            // The call screen's trusted-location cache may hold this company's records already.
            invalidateTrustedLocationCache(row.companyId);
            App.showPopupNotification('Location saved. Calls to this address will use this pin.', 'success');
          },
          () => {
            App.showPopupNotification('Failed to save the location.', 'error');
          }
        );
      },
      () => {
        App.showPopupNotification('Failed to look up the address for pin placement.', 'error');
      }
    );
  }, []);

  const handleRowAction = useCallback(
    ({ action, row }: { action: string; row: any }) => {
      if (action === 'open') {
        handleOpenEntity(row.entityType, row.entityId);
        return;
      }
      if (action === 'placePin') {
        handlePlacePin(row as AddressReviewItem);
        return;
      }
      if (action === 'openNewWindow') {
        const path = buildEntityPath(row.entityType, row.entityId);
        if (path) {
          window.open(path, '_blank', 'noopener');
        }
        return;
      }
      if (action === 'history') {
        setHistoryEntity({ entityType: row.entityType, entityId: row.entityId, items: null });
        api.getAddressVerificationAudit(
          row.entityType,
          row.entityId,
          (result: any) => {
            setHistoryEntity((prev) =>
              prev && prev.entityType === row.entityType && prev.entityId === row.entityId
                ? { ...prev, items: result?.items ?? [] }
                : prev
            );
          },
          () => {
            setHistoryEntity(null);
            App.showPopupNotification('Failed to load verification history.', 'error');
          }
        );
        return;
      }
      if (action === 'markVerified') {
        api.markAddressVerified(
          row.entityType,
          row.entityId,
          () => {
            setItems((prev) =>
              prev.filter((it) => !(it.entityType === row.entityType && it.entityId === row.entityId))
            );
            App.showPopupNotification('Address marked verified. Switch the status filter to Manually Verified to undo.', 'success');
          },
          () => {
            App.showPopupNotification('Failed to mark address verified.', 'error');
          }
        );
        return;
      }
      if (action === 'removeConfirmation') {
        if (row.entityType !== 'CALL_CARD') return;
        api.undoCallCardLocationPin(
          row.entityId,
          () => {
            App.showPopupNotification('Confirmation removed.', 'success');
            refresh();
          },
          (result: any) => {
            // A server response already produced its own message via ajaxCall.
            if (result == null) {
              App.showPopupNotification('The confirmation could not be removed. Check your connection and try again.', 'error');
            }
          }
        );
        return;
      }
      if (action === 'undoVerified') {
        // Dispatcher-asserted MANUAL rows (pin drag / alternate pick at save time) have no
        // verifiedBy and were never supervisor-flagged — nothing to undo.
        if (row.verifiedBy == null) {
          App.showPopupNotification('This record was positioned by the dispatcher at save time and has no verification to remove.', 'error');
          return;
        }
        api.unmarkAddressVerified(
          row.entityType,
          row.entityId,
          () => {
            setItems((prev) =>
              prev.filter((it) => !(it.entityType === row.entityType && it.entityId === row.entityId))
            );
            App.showPopupNotification('Verification removed. The record is back in the Low Confidence queue.', 'success');
          },
          () => {
            App.showPopupNotification('Failed to remove verification.', 'error');
          }
        );
      }
    },
    [handleOpenEntity, refresh]
  );

  const rowActions: KstDataGridRowAction[] = useMemo(() => {
    if (statusFilter === 'LOW') return [openAction, newWindowAction, historyAction, markVerifiedAction];
    if (statusFilter === 'MANUAL') return [openAction, newWindowAction, historyAction, undoVerifiedAction];
    // No Mark Verified: an on-scene confirmation is already the strongest evidence; it can only be removed.
    if (statusFilter === 'OFFICER_ON_SCENE') return [openAction, newWindowAction, historyAction, removeConfirmationAction];
    if (statusFilter === 'NO_TRUSTED') {
      // No Mark Verified here — these records have no trusted coordinate to bless. Place Pin is
      // the action: it saves a MANUAL coordinate through the CAD/MDT-authorized locationpin
      // endpoints (the client-record edit screen is read-only outside the Admin module).
      return [openAction, newWindowAction, historyAction, placePinAction];
    }
    return [openAction, newWindowAction, historyAction];
  }, [statusFilter]);

  // The no-trusted queue only spans clients/sites; narrow the entity dropdown and reset an
  // incompatible selection when switching into it.
  const entityOptions = useMemo(
    () => (statusFilter === 'NO_TRUSTED'
      ? ENTITY_TYPE_OPTIONS.filter((o) => NO_TRUSTED_ENTITY_VALUES.includes(o.value))
      : ENTITY_TYPE_OPTIONS),
    [statusFilter]
  );
  const handleStatusChange = useCallback((val: string) => {
    setStatusFilter(val);
    if (val === 'NO_TRUSTED') {
      setEntityTypeFilter((prev) => (NO_TRUSTED_ENTITY_VALUES.includes(prev) ? prev : ''));
    }
  }, []);

  const rows: KstDataGridRows = useMemo(() => ({ entries: items }), [items]);

  const closeOpenEntity = useCallback(() => setOpenEntity(null), []);
  const closeHistory = useCallback(() => setHistoryEntity(null), []);
  const historyRows: KstDataGridRows = useMemo(
    () => ({ entries: historyEntity ? historyEntity.items : [] }),
    [historyEntity]
  );

  const modalTitle = useMemo(() => {
    if (!openEntity) return '';
    return formatEntityType(openEntity.entityType);
  }, [openEntity]);

  // Pilot guard: if the geocodio proxy is off for this company, this screen is not part of
  // their feature set. Render nothing so the experience matches a non-pilot build exactly,
  // even if the URL is reached directly. (The menu link is hidden in app-compat.ts.)
  if (!App.useGeocodingProxy()) {
    return null;
  }

  return (
    <div className={theme}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">Address Review</h3>
                <p className="kst-page-description">
                  Review records flagged by the geocoder. Mark a record verified when its location is
                  correct, or open it to fix the address.
                </p>
              </div>
  
              {/* Filter bar. Note: the kst-filter-panel classes are scoped to
                  #ActivityReportSection only, so this uses explicit layout instead. */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <div style={{ minWidth: '190px' }}>
                  <label className="kstc-label" style={{ display: 'block', marginBottom: '4px' }}>Status</label>
                  <Dropdownlist
                    value={statusFilter}
                    items={STATUS_OPTIONS}
                    labelKey="label"
                    valueKey="value"
                    sortItems={false}
                    searchable={false}
                    onChange={(val) => handleStatusChange(val as string)}
                  />
                </div>
                <div style={{ minWidth: '190px' }}>
                  <label className="kstc-label" style={{ display: 'block', marginBottom: '4px' }}>Entity</label>
                  <Dropdownlist
                    value={entityTypeFilter}
                    items={entityOptions}
                    labelKey="label"
                    valueKey="value"
                    sortItems={false}
                    searchable={false}
                    onChange={(val) => setEntityTypeFilter((val as string) ?? '')}
                  />
                </div>
                {statusFilter !== 'NO_TRUSTED' ? (
                  <div style={{ minWidth: '130px' }}>
                    <label className="kstc-label" style={{ display: 'block', marginBottom: '4px' }}>Last</label>
                    <Dropdownlist
                      value={daysFilter}
                      items={DAYS_OPTIONS}
                      labelKey="label"
                      valueKey="value"
                      sortItems={false}
                      searchable={false}
                      onChange={(val) => setDaysFilter(Number(val))}
                    />
                  </div>
                ) : null}
                <button type="button" className="kstc-btn kstc-btn--secondary" onClick={refresh} disabled={loading}>
                  Refresh
                </button>
              </div>
  
              <KstDataGrid
                columns={columns}
                rows={rows}
                loading={loading}
                showheader={false}
                mobiletitlefield="formattedAddress"
                rowactions={rowActions}
                onRowAction={handleRowAction}
              />
  
              <PageModal
                open={openEntity !== null}
                title={modalTitle}
                modalId="addressReviewEntityPageModal"
                onClose={closeOpenEntity}
              >
                {openEntity && openEntity.entityType === 'NPSD_PERSON' && (
                  <NPSDPersonScreen
                    personId={openEntity.entityId}
                    isWindow={true}
                    onClose={closeOpenEntity}
                  />
                )}
                {openEntity && openEntity.entityType === 'NPSD_BUSINESS' && (
                  <NPSDBusinessScreen
                    businessId={openEntity.entityId}
                    isModal={true}
                    onClose={closeOpenEntity}
                  />
                )}
                {openEntity && openEntity.entityType === 'TOUR_STOP' && (
                  <TourStopCompletionScreen completionId={openEntity.entityId} />
                )}
                {openEntity && openEntity.entityType === 'REPORT' && (
                  <CaseReportFormScreen
                    reportId={String(openEntity.entityId)}
                    isLog={true}
                    viewOnly={false}
                  />
                )}
                {openEntity && openEntity.entityType === 'NPSD_CLIENT' && (
                  <NPSDClientScreenWrapper clientId={String(openEntity.entityId)} />
                )}
              </PageModal>
  
              <BaseFormModal
                open={historyEntity !== null}
                title={`Verification History — ${historyEntity ? formatEntityType(historyEntity.entityType) : ''}`}
                showSave={false}
                cancelText="Close"
                onCancel={closeHistory}
                hint="Supervisor verification actions for this record, newest first. Dispatcher pin adjustments at save time are not verifications and do not appear here."
              >
                <KstDataGrid
                  columns={historyColumns}
                  rows={historyRows}
                  loading={historyEntity !== null && historyEntity.items === null}
                  showheader={false}
                  mobiletitlefield="dateCreated"
                />
              </BaseFormModal>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).SupervisorAddressReviewScreenReact = SupervisorAddressReviewScreen;
